<template>
  <v-slider
    class="fill-opacity"
    :class="{'nothing-to-reset': !canReset}"
    label="Fill opacity"
    :model-value="modelValue"
    :min="MIN_PERCENT"
    :max="100"
    :step="5"
    append-icon="mdi-restore"
    thumb-label
    hide-details
    color="primary"
    density="compact"
    :ripple="false"
    data-testid="fill-opacity-slider"
    thumb-size="18"
    @update:model-value="onInput"
    @end="onEnd"
    @click:append="reset"
  >
    <template #thumb-label>
      {{ modelValue }}%
    </template>
  </v-slider>
</template>

<script lang="ts">
import {defineComponent} from 'vue';

type Timer = ReturnType<typeof setTimeout> | undefined;

/**
 * The floor is not zero: invisible fills are still clickable, so a layer at 0% would open info windows from
 * what looks like an empty map.
 */
const MIN_PERCENT = 10;

/** Arrow keys move the thumb without ever reporting a release, so a change left alone this long counts as done. */
const IDLE_MS = 500;

export default defineComponent({
  name: 'FillOpacitySlider',
  props: {
    modelValue: {type: Number, required: true},
  },
  emits: ['update:modelValue', 'commit'],
  data() {
    return {
      MIN_PERCENT,
      idleTimer: undefined as Timer,
      uncommitted: undefined as {percent: number} | undefined,
    };
  },
  computed: {
    canReset(): boolean {
      return this.modelValue !== 100;
    },
  },
  beforeUnmount() {
    this.commit();
  },
  methods: {
    onInput(percent: number) {
      this.change(percent);
      clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => this.commit(), IDLE_MS);
    },
    onEnd(percent: number) {
      this.change(percent);
      this.commit();
    },
    reset() {
      if (!this.canReset) return;
      this.change(100);
      this.commit();
    },
    /* The prop only catches up on the next render, so the value to commit is held here rather than read back. */
    change(percent: number) {
      this.uncommitted = {percent};
      this.$emit('update:modelValue', percent);
    },
    commit() {
      clearTimeout(this.idleTimer);
      if (!this.uncommitted) return;
      const {percent} = this.uncommitted;
      this.uncommitted = undefined;
      this.$emit('commit', percent);
    },
  },
});
</script>

<style scoped lang="scss">
.fill-opacity :deep(.v-label) {
  font-family: main.$fontPrimary;
  font-size: 13px;
  font-weight: 500;
  opacity: 1;
}
.fill-opacity :deep(.v-input__append) {
  opacity: 1;
  cursor: pointer;
}
.fill-opacity.nothing-to-reset :deep(.v-input__append) {
  opacity: 0.4;
  cursor: default;
}
.fill-opacity.fill-opacity {
  margin-inline: 0;
}
.fill-opacity :deep(.v-input__prepend) {
  margin-inline-end: 0;
}
</style>
